/**
 * CALL-CENTER-AGENT-SCOPE-AND-DISPOSITION-CORE-V1 -- the disposition panel
 * pure view-model (FE mirror of lib/domain/lead/lead-disposition.ts). NO
 * React, no fetch, no Date.now() -- callers inject the vocabulary + the
 * current selection; this module computes which tiers render, clears stale
 * downstream selections on an upstream change, and reports Save validity.
 *
 * The dependency rules are the SAME the server enforces (the server is the
 * boundary; this keeps the UI honest so it never OFFERS an invalid save):
 *   method (no default) -> communication result (no default) -> connect
 *   result (Connected only, required) -> interested detail (interested
 *   variants only, optional) -> next action (required) + callback date/time
 *   (schedule-callback only, required) + note (terminal closures, required).
 */

import { istWallClockToUtcIso, isWallClockValue } from '@/lib/datetime-ist';

// ---- Vocabulary shapes (lockstep mirror of the backend) --------------------

export interface DispositionResultOption {
  readonly id: string;
  readonly label: string;
  readonly connected: boolean;
  /**
   * LEAD-WORKFLOW-TRUTH-CORRECTIVE-V1 (M1-M3): the lawful next-action ids for
   * this (method, result) cell (config-backed; the SERVER is authoritative).
   * Omitted = unconstrained. The panel filters its picklist to this set as a
   * convenience so it never OFFERS a save the server would reject. For a
   * CONNECTED result the connect-result's set governs instead.
   */
  readonly allowedNextActionIds?: readonly string[];
}
export interface DispositionMethodOption {
  readonly id: string;
  readonly label: string;
  readonly results: readonly DispositionResultOption[];
}
export interface DispositionConnectOption {
  readonly id: string;
  readonly label: string;
  readonly interested: boolean;
  /** LEAD-WORKFLOW-TRUTH-CORRECTIVE-V1 (M1-M3): governs the phone+connected
   *  branch's lawful next actions (overrides the connected result's set). */
  readonly allowedNextActionIds?: readonly string[];
}
export interface DispositionDetailOption {
  readonly id: string;
  readonly label: string;
}
export interface DispositionNextActionOption {
  readonly id: string;
  readonly label: string;
  readonly requiresCallbackAt: boolean;
  readonly requiresNote: boolean;
  readonly terminalStage: 'lost' | null;
}
export interface DispositionVocabulary {
  readonly methods: readonly DispositionMethodOption[];
  readonly connectResults: readonly DispositionConnectOption[];
  readonly interestedDetails: readonly DispositionDetailOption[];
  readonly nextActions: readonly DispositionNextActionOption[];
}

// ---- The selection state (URL-free; component-local) -----------------------

export interface DispositionSelection {
  readonly method: string | null;
  readonly resultId: string | null;
  readonly connectId: string | null;
  readonly detailId: string | null;
  readonly nextActionId: string | null;
  /** The datetime-local value as typed (IST wall-clock, no zone);
   *  dispositionSaveBody sends it as a UTC instant. */
  readonly callbackAtIso: string | null;
  readonly note: string;
}

export const EMPTY_DISPOSITION_SELECTION: DispositionSelection = Object.freeze({
  method: null,
  resultId: null,
  connectId: null,
  detailId: null,
  nextActionId: null,
  callbackAtIso: null,
  note: '',
});

// ---- Upstream-clears-downstream reducers (the charter rule) ----------------

/** Change the METHOD: the result vocabulary is replaced and EVERY dependent
 *  selection is cleared (no stale child value survives). */
export function selectMethod(
  _prev: DispositionSelection,
  method: string,
): DispositionSelection {
  return { ...EMPTY_DISPOSITION_SELECTION, method };
}

/** Change the communication RESULT: connect/detail cleared; next action +
 *  callback + note cleared (a new result can change the whole downstream). */
export function selectResult(
  prev: DispositionSelection,
  resultId: string,
): DispositionSelection {
  return {
    ...EMPTY_DISPOSITION_SELECTION,
    method: prev.method,
    resultId,
  };
}

/** Change the CONNECT result: interested detail cleared (a non-interested
 *  connect forbids a detail). Next action stays (it is independent). */
export function selectConnect(
  prev: DispositionSelection,
  connectId: string,
): DispositionSelection {
  return { ...prev, connectId, detailId: null };
}

export function selectDetail(
  prev: DispositionSelection,
  detailId: string,
): DispositionSelection {
  return { ...prev, detailId };
}

/** Change the NEXT action: callback + note cleared (their requirement is
 *  action-specific, so a stale value never rides a new action). */
export function selectNextAction(
  prev: DispositionSelection,
  nextActionId: string,
): DispositionSelection {
  return { ...prev, nextActionId, callbackAtIso: null, note: '' };
}

// ---- Derived render state (which tiers show; is Save valid) ----------------

export interface DispositionPanelModel {
  readonly method: DispositionMethodOption | null;
  readonly results: readonly DispositionResultOption[];
  readonly result: DispositionResultOption | null;
  /** Connected result chosen -> the connect tier renders (required). */
  readonly showConnect: boolean;
  readonly connect: DispositionConnectOption | null;
  /** Interested connect chosen -> the interested-detail tier renders. */
  readonly showDetail: boolean;
  /**
   * LEAD-WORKFLOW-TRUTH-CORRECTIVE-V1 (M1-M3): the next actions LAWFUL for the
   * current (method, result [, connect]) cell -- the FILTERED picklist the
   * panel renders (a convenience mirror of the server family rule). When the
   * cell is unconstrained this is the full vocabulary list.
   */
  readonly nextActions: readonly DispositionNextActionOption[];
  readonly nextAction: DispositionNextActionOption | null;
  readonly showCallback: boolean;
  readonly showNote: boolean;
  /** Save is enabled ONLY when the whole chosen path is complete AND the chosen
   *  next action is lawful for the cell (mirrors the server). */
  readonly canSave: boolean;
}

/** True for a bounded, digit-guarded closure note (the FE mirror of
 *  validateDispositionNote -- 7+ digit runs rejected so a phone can never
 *  be typed into a note; the server re-checks). */
export function isNoteValid(note: string): boolean {
  const trimmed = note.trim();
  if (trimmed.length === 0 || trimmed.length > 200) return false;
  for (const c of trimmed) {
    const code = c.charCodeAt(0);
    if (code < 32 || code > 126) return false;
  }
  return !/\d{7,}/.test(trimmed.replace(/[\s()+-]/g, ''));
}

export function buildDispositionPanelModel(
  vocabulary: DispositionVocabulary,
  sel: DispositionSelection,
): DispositionPanelModel {
  const method =
    vocabulary.methods.find((m) => m.id === sel.method) ?? null;
  const results = method?.results ?? [];
  const result = results.find((r) => r.id === sel.resultId) ?? null;

  const showConnect = result?.connected === true;
  const connect = showConnect
    ? vocabulary.connectResults.find((c) => c.id === sel.connectId) ?? null
    : null;
  const showDetail = connect?.interested === true;

  // LEAD-WORKFLOW-TRUTH-CORRECTIVE-V1 (M1-M3): the lawful next actions for the
  // current cell. The connect-result's set governs a connected branch;
  // otherwise the result's set; an omitted set means unconstrained (the full
  // list). Mirrors validateDispositionChain -- the server stays authoritative.
  const effectiveAllowed =
    (connect !== null ? connect.allowedNextActionIds : undefined) ??
    result?.allowedNextActionIds;
  const nextActions =
    effectiveAllowed === undefined
      ? vocabulary.nextActions
      : vocabulary.nextActions.filter((n) => effectiveAllowed.includes(n.id));

  const nextAction =
    vocabulary.nextActions.find((n) => n.id === sel.nextActionId) ?? null;
  const showCallback = nextAction?.requiresCallbackAt === true;
  const showNote = nextAction?.requiresNote === true;

  // Save validity re-derives the whole chain (the server re-validates):
  const methodOk = method !== null;
  const resultOk = result !== null;
  const connectOk = !showConnect || connect !== null;
  const nextAllowed =
    effectiveAllowed === undefined ||
    (nextAction !== null && effectiveAllowed.includes(nextAction.id));
  const nextOk = nextAction !== null && nextAllowed;
  const callbackOk =
    !showCallback || (sel.callbackAtIso !== null && sel.callbackAtIso.length > 0);
  const noteOk = !showNote || isNoteValid(sel.note);

  return {
    method,
    results,
    result,
    showConnect,
    connect,
    showDetail,
    nextActions,
    nextAction,
    showCallback,
    showNote,
    canSave: methodOk && resultOk && connectOk && nextOk && callbackOk && noteOk,
  };
}

/** The promised callback as a UTC instant. The datetime-local value is IST
 *  wall-clock, so it goes through the ONE IST converter (the COC
 *  RecordOutcomeDialog path) -- sending it bare let the server read it in its
 *  own timezone and move the promised time. A value that already carries a
 *  zone passes through unchanged. */
export function callbackInstantForSave(value: string): string {
  return isWallClockValue(value) ? istWallClockToUtcIso(value) : value;
}

/** The wire body for POST /api/leads/:id/disposition from a VALID selection.
 *  Callers guard on model.canSave before sending. */
export function dispositionSaveBody(sel: DispositionSelection): {
  method: string;
  resultId: string;
  connectId?: string;
  detailId?: string;
  nextActionId: string;
  callbackAt?: string;
  note?: string;
} {
  return {
    method: sel.method as string,
    resultId: sel.resultId as string,
    ...(sel.connectId !== null ? { connectId: sel.connectId } : {}),
    ...(sel.detailId !== null ? { detailId: sel.detailId } : {}),
    nextActionId: sel.nextActionId as string,
    ...(sel.callbackAtIso !== null
      ? { callbackAt: callbackInstantForSave(sel.callbackAtIso) }
      : {}),
    ...(sel.note.trim().length > 0 ? { note: sel.note.trim() } : {}),
  };
}
